import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Calendar from "../../../components/calendar/Calendar";
import ReservationSelector from "../../../components/reservationSelector/ReservationSelector";
import FormReservation from "../../../components/formReservation/FormReservation";
import {
  fetchDisponibiliteJour,
  fetchDisponibiliteMois,
  clearDisponibiliteMois,
} from "../../../features/reservation";

const formatDateForApi = (value) => {
  if (!value) return null;
  const dateObj = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return null;
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const deriveDateFromReservation = (reservation) => {
  if (!reservation) return new Date();
  const source =
    reservation?.date_atelier ||
    reservation?.date ||
    reservation?.schedule_date ||
    null;
  if (!source) return new Date();
  const parsed = source instanceof Date ? source : new Date(source);
  if (Number.isNaN(parsed.getTime())) return new Date();
  return parsed;
};

const deriveInitialCreneau = (reservation) => {
  if (!reservation) return null;
  if (reservation?.creneau) return reservation.creneau;
  return {
    creneau_heure_debut:
      reservation?.creneau_heure_debut ??
      reservation?.heure_debut ??
      reservation?.creneau?.heure ??
      null,
    creneau_heure_fin:
      reservation?.creneau_heure_fin ??
      reservation?.heure_fin ??
      reservation?.creneau?.fin ??
      null,
  };
};

const deriveCreneauId = (reservation) => {
  if (!reservation) return null;
  return (
    reservation?.creneau_id ??
    reservation?.creneau?.creneau_id ??
    reservation?.creneau?.id ??
    reservation?.id ??
    null
  );
};

const EditReservationModal = ({
  open,
  reservation,
  onClose,
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const disponibiliteMois = useSelector(
    (state) => state.reservation.creneaux || []
  );
  const ateliers = useSelector(
    (state) => state.reservation.ateliers || []
  );

  const [dateSelectionnee, setDateSelectionnee] = useState(new Date());
  const [selectionData, setSelectionData] = useState({});
  const [atelierSelectionne, setAtelierSelectionne] = useState("");
  const [creneauSelectionne, setCreneauSelectionne] = useState("");

  const resolvedAtelierId = useMemo(() => {
    if (!reservation) return "";
    if (reservation?.atelier_id) return reservation.atelier_id;
    if (reservation?.atelierId) return reservation.atelierId;
    if (reservation?.atelier_uuid) return reservation.atelier_uuid;
    if (reservation?.atelier) {
      const match =
        ateliers.find(
          (item) =>
            (item?.nom || "").toLowerCase().trim() ===
            reservation.atelier.toLowerCase().trim()
        ) || null;
      if (match) {
        return (
          match.atelier_id ??
          match.id ??
          match._id ??
          ""
        );
      }
    }
    return "";
  }, [reservation, ateliers]);

  const initialCreneau = useMemo(
    () => deriveInitialCreneau(reservation),
    [reservation]
  );
  const initialCreneauId = useMemo(
    () => deriveCreneauId(reservation),
    [reservation]
  );
  const initialDateValue = useMemo(
    () => deriveDateFromReservation(reservation),
    [reservation]
  );

  useEffect(() => {
    if (!open || !reservation) return;
    const initialDate = initialDateValue;
    setDateSelectionnee(initialDate);
    setSelectionData({
      date: initialDate,
      atelierId: resolvedAtelierId || "",
      atelierNom: reservation?.atelier || "",
      participants: Number(reservation?.nombre_inscrit) || 1,
      creneau: initialCreneau,
      creneauId: initialCreneauId,
    });
    setAtelierSelectionne(resolvedAtelierId || "");
    setCreneauSelectionne(initialCreneauId || "");
  }, [
    open,
    reservation,
    resolvedAtelierId,
    initialCreneau,
    initialCreneauId,
    initialDateValue,
  ]);

  const handleDateChange = useCallback((value) => {
    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) return;
    setDateSelectionnee(parsed);
    setSelectionData((prev) => ({
      ...prev,
      date: parsed,
    }));
  }, []);

  const handleSelectionChange = useCallback((selection) => {
    setSelectionData(selection || {});
    if (Object.prototype.hasOwnProperty.call(selection, "atelierId")) {
      setAtelierSelectionne(selection.atelierId || "");
    }
    if (Object.prototype.hasOwnProperty.call(selection, "creneauId")) {
      setCreneauSelectionne(selection.creneauId || "");
    }
  }, []);

  const handleMonthChange = useCallback(
    ({ year, month }) => {
      const targetAtelier = atelierSelectionne || resolvedAtelierId || "";
      if (!targetAtelier || !year || !month) return;
      dispatch(clearDisponibiliteMois());
      dispatch(
        fetchDisponibiliteMois({
          annee: year,
          mois: month,
          atelier: targetAtelier,
        })
      );
    },
    [dispatch, atelierSelectionne, resolvedAtelierId]
  );

  useEffect(() => {
    if (!open || !dateSelectionnee) return;
    const dateFormate = formatDateForApi(dateSelectionnee);
    if (!dateFormate) return;
    const targetAtelier = atelierSelectionne || resolvedAtelierId || "";
    dispatch(
      fetchDisponibiliteJour({
        date: dateFormate,
        atelier: targetAtelier,
      })
    );
    dispatch(
      fetchDisponibiliteMois({
        annee: dateSelectionnee.getFullYear(),
        mois: dateSelectionnee.getMonth() + 1,
        atelier: targetAtelier,
      })
    );
  }, [
    open,
    dateSelectionnee,
    atelierSelectionne,
    resolvedAtelierId,
    dispatch,
  ]);

  const handleSubmitEdit = useCallback(
    async ({ client_data, commentaire }) => {
      if (typeof onSubmit !== "function") {
        return {
          success: false,
          message: "Aucun gestionnaire de mise à jour défini.",
        };
      }
      return onSubmit({
        reservation,
        client_data,
        commentaire,
        selection: selectionData,
        atelierId: atelierSelectionne || resolvedAtelierId || "",
        creneauId: creneauSelectionne,
      });
    },
    [
      onSubmit,
      reservation,
      selectionData,
      atelierSelectionne,
      resolvedAtelierId,
      creneauSelectionne,
    ]
  );

  if (!open || !reservation) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl p-6 space-y-6 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-2xl font-semibold text-slate-900">
              Modifier la réservation
            </h3>
            <p className="text-sm text-slate-500">
              Ajustez la date, le créneau ou les informations client puis validez.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 text-2xl leading-none"
            aria-label="Fermer la fenêtre de modification"
          >
            &times;
          </button>
        </div>

        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-full">
              <Calendar
                dateSelectionnee={dateSelectionnee}
                setDateSelectionne={handleDateChange}
                disponibilite={disponibiliteMois}
                onMonthChange={handleMonthChange}
              />
            </div>
            <div className="h-full">
              <ReservationSelector
                selectedDate={dateSelectionnee}
                onSelectionChange={handleSelectionChange}
                initialAtelierId={resolvedAtelierId || ""}
                initialParticipants={reservation?.nombre_inscrit || 1}
                initialCreneauId={initialCreneauId}
                initialCreneau={initialCreneau}
                initialDate={initialDateValue}
                preserveInitialSelection
              />
            </div>
          </div>
          <FormReservation
            creneauSelectionne={creneauSelectionne}
            formData={selectionData}
            atelierSelectionne={atelierSelectionne || resolvedAtelierId || ""}
            dateSelectionnee={dateSelectionnee}
            onSubmitReservation={handleSubmitEdit}
            initialClientData={reservation?.client_data}
            initialCommentaire={reservation?.commentaire || ""}
            submitLabel="Modifier"
          />
        </div>
      </div>
    </div>
  );
};

export default EditReservationModal;
